import { useQuery } from "@tanstack/react-query";

import { ChatStatus, type ChatChannelWithLastMessage } from "@fitness-app/data-models/entities/Chat";

import { useUserClaims } from "~/hooks/trainer/useUserClaims";
import { pendingChatSuggestionChannelsQueryKey } from "~/modules/Chat/hooks/chatAgentSuggestionQueryKeys";
import { supabase, useAppSelector } from "~/store/initializeStore";

type PendingSuggestionMetadataRow = {
  channelId: string | null;
  agentReplyGeneratedAt: string | null;
  createdAt: string;
};

type PendingChatSuggestionChannelsResult = {
  channels: ChatChannelWithLastMessage[];
  count: number;
};

export const usePendingChatSuggestionChannels = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const userId = useAppSelector((store) => store.user.data?.id);
  const { memberId } = useUserClaims();

  return useQuery({
    queryKey: [...pendingChatSuggestionChannelsQueryKey, userId, memberId],
    keepPreviousData: true,
    enabled: !!memberId && enabled && Boolean(userId),
    refetchInterval: enabled ? 15000 : false,
    queryFn: async (): Promise<PendingChatSuggestionChannelsResult> => {
      if (!userId) {
        return {
          channels: [],
          count: 0,
        };
      }

      const { data: metadataRows, error: metadataError } = await supabase
        .from("chat_message_metadata")
        .select("channelId, agentReplyGeneratedAt, createdAt")
        .eq("agentReplyStatus", "suggested")
        .eq("agentReplyOwnerTeamMemberId", memberId || "")
        .not("channelId", "is", null)
        .order("agentReplyGeneratedAt", { ascending: false })
        .order("createdAt", { ascending: false })
        .returns<PendingSuggestionMetadataRow[]>();

      if (metadataError) {
        throw new Error(metadataError.message);
      }

      const seenChannelIds = new Set<string>();
      const orderedChannelIds = (metadataRows || []).reduce<string[]>((result, row) => {
        if (row.channelId && !seenChannelIds.has(row.channelId)) {
          seenChannelIds.add(row.channelId);
          result.push(row.channelId);
        }

        return result;
      }, []);

      if (!orderedChannelIds.length) {
        return {
          channels: [],
          count: 0,
        };
      }

      const { data: channels, error: channelsError } = await supabase
        .from("chat_channel")
        .select(
          "*, lastMessage:lastMessageId(id, content, createdAt, files), profiles:user_profile!inner(avatarUrl, firstName, lastName, id, email)",
        )
        .contains("assignedUsers", [userId])
        .eq("status", ChatStatus.Active)
        .in("id", orderedChannelIds)
        .returns<ChatChannelWithLastMessage[]>();

      if (channelsError) {
        throw new Error(channelsError.message);
      }

      const channelOrder = new Map(orderedChannelIds.map((id, index) => [id, index]));
      const sortedChannels = [...(channels || [])].sort(
        (left, right) =>
          (channelOrder.get(left.id) ?? Number.MAX_SAFE_INTEGER) -
          (channelOrder.get(right.id) ?? Number.MAX_SAFE_INTEGER),
      );

      return {
        channels: sortedChannels,
        count: sortedChannels.length,
      };
    },
  });
};
